import { z } from "zod"
import { coreTextLimits } from "@ibada/shared-types/common"
import { DOCUMENT_LANGUAGE_VALUES, DOCUMENT_TIMEZONE_VALUES, type DocumentLanguage, type DocumentTimezone } from "@ibada/shared-types/workspace"
import { BranchStatus } from "@/features/church/config/branches/types/branch.types"

export type BranchFormInput = {
  name: string
  code: string
  details?: string
  phoneNumber?: string
  principalMemberId?: string
  /** OFFICIAL document language for content generated for this branch. */
  language: DocumentLanguage
  /** OFFICIAL document IANA timezone for content generated for this branch. */
  timezone: DocumentTimezone
  status: BranchStatus
  /**
   * Should the person creating this branch administer it? Default NO.
   *
   * A branch is a whole organisation, not a folder: being its administrator means its notifications, its
   * approvals, and — under BRANCH_AUTONOMY — its figures. Whoever should run it gets invited to it.
   */
  grantCreatorAccess: boolean
}

type CreateBranchFormSchemaParams = {
  nameMin: string
  nameMax: string
  detailsMax: string
  phoneInvalid: string
  codeInvalid: string
}

const SIMPLE_PHONE_REGEX = /^\+?[0-9()\-\s.]{7,20}$/
const BRANCH_CODE_REGEX = new RegExp(`^[A-Z0-9]{${coreTextLimits.branchCode}}$`)

export function suggestBranchCode(name: string): string {
  const letters = name
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
  if (letters.length === 0) return ""
  return letters.slice(0, 3).padEnd(3, "X")
}

export function createBranchFormSchema({ nameMin, nameMax, detailsMax, phoneInvalid, codeInvalid }: CreateBranchFormSchemaParams) {
  return z.object({
    name: z.string().trim().min(2, nameMin).max(coreTextLimits.branchName, nameMax),
    code: z.string().trim().regex(BRANCH_CODE_REGEX, codeInvalid),
    details: z.string().trim().max(coreTextLimits.branchDescription, detailsMax).optional().or(z.literal("")),
    phoneNumber: z
      .string()
      .trim()
      .refine((value) => value.length === 0 || SIMPLE_PHONE_REGEX.test(value), phoneInvalid)
      .optional()
      .or(z.literal("")),
    principalMemberId: z.string().trim().optional().or(z.literal("")),
    language: z.enum(DOCUMENT_LANGUAGE_VALUES),
    timezone: z.enum(DOCUMENT_TIMEZONE_VALUES),
    status: z.nativeEnum(BranchStatus),
    grantCreatorAccess: z.boolean(),
  })
}
